import { useEffect, useMemo, useState } from "react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { RequirementCard } from "../../components/requirementss/RequirementCard";
import { RequirementForm, RequirementFormValues } from "../../components/requirements/RequirementForm";
import { RequirementDetailPanel } from "../../components/requirementss/RequirementDetailPanel";
import { DealDeskState, LenderTag, Requirement, StatusHistoryEntry } from "../../types/deals";
import { Clipboard, BadgeCheck, IndianRupee, TrendingUp } from "lucide-react";
import apiService from "../../services/apiService";
import { useParams } from "react-router-dom";
import { RMList } from "../../utils/interfaces";
import { EnhancedViewTaggedLenderDialog } from "../../components/requirementss/EnhancedViewTaggedLenderDialog";
import { LenderStatusDialog, StatusFormValues } from "../../components/requirementss/LenderStatusDialog";
import { useCustomerWorkflow } from "../../context/CustomerWorkflowContext";



interface RMListResponse {
  data: RMList[];
}

interface LenderTypesResponse {
  data: LenderTypes[];
}
interface ApiResponse<T> {
  data: T;
  message?: string;
  status?: number;
  // financial?:FinancialTabProps
}

interface LenderTypes {
  id: number;
  name: string;
  display_order: number;
  parent_type: string;
};

interface LenderCompanyList {
  id: number;
  name: string;
}



const Login = ({handleViewRequirement, adminUserList}: {handleViewRequirement: (requirementData: any) => void, adminUserList: RMList[]}) => {
  const { customerId } = useParams();
  const [state, setState] = useState<DealDeskState>();
  const [LenderStatus, setLenderStatus] = useState<any>();
  const [formOpen, setFormOpen] = useState(false);
  const [editingRequirement, setEditingRequirement] = useState<Requirement | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [lenderCompanyList, setLenderCompanyList] = useState<LenderCompanyList[]>([]);
  const [lender_user_product_types, setLenderUserProductType] = useState<LenderTypes[]>([]);
  const [viewLender, setViewLender] = useState<LenderTag | null>(null);
  const [statusOpenFor, setStatusOpenFor] = useState<string | null>(null);
  const [requirementId, setRequirementId] = useState<string | null>(null);
  const [isAddingRequirement, setIsAddingRequirement] = useState(false);
  const [isFetchingRequirements, setIsFetchingRequirements] = useState(false);
  const [requirementStatuses, setRequirementStatuses] = useState<any[]>([]);
  const { refreshWorkflow } = useCustomerWorkflow();
  useEffect(() => { fetchCustomerRequirement(), fetchLenderStatus(), fetchLenderUserProductTypes() }, []);

     // fetch all data on mount
     useEffect(() => {
      const fetchData = async () => {
        // setLoading(true);
        try {
          const [productRes, statusRes] = await Promise.all([
            apiService.get<LenderTypes[]>("/lender/get-lender-user-product-types"),
            apiService.get<any>("/login/get-lender-status"),
          ]);
          setLenderUserProductType((productRes as any)?.data || productRes || []);
          setLenderStatus(statusRes?.data || []);
        } catch (err) {
          console.error("Error fetching data:", err);
        } finally {
          // setLoading(false);
        }
      };
  
      fetchData();
    }, []);

  // const fetchLenderCompanyList = async () => {
  //   try {
  //     const response = await apiService.get<{ data: LenderCompanyList[] }>(`/customers/get-lender-company-list`);
  //     const transformedDocs = response.data.map((doc) => ({
  //       name: doc.name,
  //       id: doc.id
  //     }));
  //     setLenderCompanyList(transformedDocs);
  //   } catch (error) {
  //     console.error("Error fetching lender companies:", error);
  //   }
  // };


  async function handleStatusUpdate(lenderId: string, vals: StatusFormValues) {

    if (!requirementId) return; // safety check
    const now = new Date().toISOString();
  
    // Find the requirement in the state
    const requirement = state?.requirements?.find(
      req => String(req.id) === String(requirementId)
    );
    if (!requirement) return; // requirement not found
  
    // Update lenders
    const updatedLenders = requirement.lenders.map(l => {
      if (String(l.id) !== String(lenderId)) return l;
  
      const entry: StatusHistoryEntry = {
        statusId: Number(vals.status!),
        timestamp: now,
        updatedBy: requirement.applicationRM,
        note: vals.note,
        reason: vals.reason,
      };
  
      // Handle term sheet
      if (vals.termSheetFile) {
        const file = vals.termSheetFile as File;
        entry.termSheetName = file.name;
        entry.termSheetUrl = URL.createObjectURL(file);
      }
  
      // Handle sanction
      if (typeof vals.sanctionAmount === "number") {
        entry.sanctionAmount = vals.sanctionAmount;
      }
      const sanctionDate = (vals as any).sanctionDate;
      if (sanctionDate) {
        entry.sanctionDate = sanctionDate instanceof Date
          ? sanctionDate.toISOString()
          : sanctionDate;
      }
      const sanctionValidTill = (vals as any).sanctionValidTill;
      if (sanctionValidTill) {
        entry.sanctionValidTill = sanctionValidTill instanceof Date
          ? sanctionValidTill.toISOString()
          : sanctionValidTill;
      }
  
      // Handle disbursed
      if (typeof vals.disbursedAmount === "number") {
        entry.disbursedAmount = vals.disbursedAmount;
      }
  
      // Handle sanction letter
      if (vals.sanctionLetterFile) {
        const file = vals.sanctionLetterFile as File;
        entry.sanctionLetterName = file.name;
        entry.sanctionLetterUrl = URL.createObjectURL(file);
      }
  
      // Calculate payouts
      if (vals.payoutBasis === "manual") {
        entry.customerPayout = vals.customerPayout ?? 0;
        entry.lenderPayout = vals.lenderPayout ?? 0;
        entry.partnerPayout = vals.partnerPayout ?? 0;
      } else {
        entry.customerPayoutPercent = vals.customerPayoutPercent ?? 0;
        entry.lenderPayoutPercent = vals.lenderPayoutPercent ?? 0;
        entry.partnerPayoutPercent = vals.partnerPayoutPercent ?? 0;
  
        entry.customerPayout = vals.customerPayout ?? 0;
        entry.lenderPayout = vals.lenderPayout ?? 0;
        entry.partnerPayout = vals.partnerPayout ?? 0;
      }
  
      entry.totalRevenue =
        (entry.customerPayout ?? 0) +
        (entry.lenderPayout ?? 0) -
        (entry.partnerPayout ?? 0);
  
      vals.totalRevenue = entry.totalRevenue ?? 0;
  
      return { ...l, statusHistory: [...l.statusHistory, entry] };
    });
  
    // Optionally update the state with new lenders
    setState(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        requirements: prev.requirements.map(r =>
          String(r.id) === String(requirementId)
            ? { ...r, lenders: updatedLenders }
            : r
        ),
      };
    });
  
    // Send update to backend
    await apiService.post('/login/lender-status-update', { lenderId, vals });
    await refreshWorkflow();
    setStatusOpenFor(null);
  };
  

  const fetchCustomerRequirement = async () => {
    if (!customerId) return;
    setIsFetchingRequirements(true);
    try {
      const response = await apiService.get<any>(
        `/login/get-requirements/${customerId}`
      );
      let result = { requirements: response }
      setState(result)
    } catch (error) {
      console.error("Error fetching customer details:", error);
      // toast.error("Failed to load customer details");
    } finally {
      setIsFetchingRequirements(false);
    }
  };

  const fetchLenderStatus = async () => {
    if (!customerId) return;

    // setLoading(true);
    try {
      const response = await apiService.get<any>(
        `/login/get-lender-status`
      );
      let result = response.data
      setLenderStatus(result)
    } catch (error) {
      console.error("Error fetching customer details:", error);
      // toast.error("Failed to load customer details");
    } finally {
      // setLoading(false);
    }
  };


  const fetchLenderUserProductTypes = async () => {
    try {
      const response = await apiService.get<LenderTypesResponse>(`/lender/get-lender-user-product-types`);
      const transformedDocs = response.data.map((doc) => ({
        name: doc.name,
        id: doc.id,
        display_order: doc.display_order,
        parent_type: doc.parent_type
      }));
      setLenderUserProductType(transformedDocs);
    } catch (error) {
      console.error("Error fetching lender types:", error);
    }
  };

  const selectedRequirement = useMemo(() => state?.requirements.find(r => r.id === detailId) ?? null, [state, detailId]);
  const recentQuantum = useMemo(() => state?.requirements[0]?.quantum, [state?.requirements]);

  const kpis = useMemo(() => {
    const totalReq = state?.requirements.length;
    let totalSanctioned = 0;
    let totalDisbursed = 0;
    let totalRevenue = 0;
    const payoutList: number[] = [];
    state?.requirements.forEach((r) => {
      r.lenders?.forEach((l) => {
        l?.statusHistory?.forEach((e) => {
          if (typeof e.sanctionAmount === "number") totalSanctioned += e.sanctionAmount;
          if (typeof e.disbursedAmount === "number") {
            totalDisbursed += e.disbursedAmount;
            if (typeof e.totalRevenue === "number") payoutList.push(e.totalRevenue);

            const disbEntry = [...l.statusHistory].reverse().find((e) => typeof e.disbursedAmount === 'number');
            if (disbEntry) {
              totalDisbursed += disbEntry.disbursedAmount ?? 0;
              totalRevenue += (disbEntry.customerPayout ?? 0) + (disbEntry.lenderPayout ?? 0) - (disbEntry.partnerPayout ?? 0);
            }
          }
        });
      });
    });


  
    const averagePayout = payoutList.length
      ? Math.round((payoutList.reduce((a, b) => a + b, 0) / payoutList.length) * 100) / 100
      : 0;
    return { totalReq, totalSanctioned, totalDisbursed, averagePayout, totalRevenue };
  }, [state?.requirements]);

  async function handleAddRequirement(values: RequirementFormValues) {
    // Prevent double-submit / multiple API calls
    if (isAddingRequirement) return;

    const newReq: Requirement = {
      type: values.type,
      product_type_id: values.products ? Number(values.products) : 0,
      asset_category_id: Number(values.assetCategory),
      quantum: values.quantum,
      tenure: values.tenure,
      applicationRM: values.applicationRM,
      dealNote: values.dealNote || '',
      createdAt: new Date().toISOString(),
      lenders: [],
      customerId: customerId || ""
    };


    try {
      setIsAddingRequirement(true);
      const response = await apiService.post<ApiResponse<any>>('/login/add-requirements', {
        newReq
      });
      // After successful add, refetch full list so counts and derived data stay correct
      await fetchCustomerRequirement();
    } catch {
    } finally {
      setIsAddingRequirement(false);
      setFormOpen(false);
    }
  }

  async function handleUpdateRequirement(values: RequirementFormValues) {
    if (!editingRequirement) return;

    const updatedReq: Requirement = {
      ...editingRequirement,
      type: values.type,
      product_type_id: Number(values.products),
      asset_category_id: Number(values.assetCategory),
      quantum: values.quantum,
      tenure: values.tenure,
      applicationRM: values.applicationRM,
      dealNote: values.dealNote,
      customerId: editingRequirement.customerId || customerId || "",
    };

    try {
      const response = await apiService.post<ApiResponse<any>>('/login/update-requirements', {
        requirementId: editingRequirement.id,
        updates: updatedReq,
      });

      if (response?.data) {
        updateRequirement(response.data);
      } else {
        updateRequirement(updatedReq);
      }
    } catch {
    } finally {
      setFormOpen(false);
      setEditingRequirement(null);
    }
  }

  const handleSubmitRequirement = (values: RequirementFormValues) => {
    if (editingRequirement) {
      return handleUpdateRequirement(values);
    }
    return handleAddRequirement(values);
  };

  function updateRequirement(updated: Requirement) {
    setState((s: any) => ({ requirements: s.requirements.map((r: any) => r.id === updated.id ? updated : r) }));
  }

  // const handleStatusEdit = (id: string, req: string) => {
  //   setStatusOpenFor(id);
  //   setRequirementId(req);

  // }

  // const handleEditRequirement = (req: Requirement) => {
  //   setEditingRequirement(req);
  //   setFormOpen(true);
  // }

  const handleFormOpenChange = (open: boolean) => {
    setFormOpen(open);
    if (!open) {
      setEditingRequirement(null);
    }
  };

  // const handleViewLender = (lender: LenderTag) => {
  //   setViewLender(lender);
  // }

  const currentRequirement = state?.requirements?.find(
    req => String(req.id) === String(requirementId)
  );
  
  const currentLender = currentRequirement?.lenders.find(
    l => String(l.id) === String(statusOpenFor)
  );


  useEffect(() => {

    async function fetchRequirementStatuses() {
      try {
        const res: any = await apiService.get<any>("/login/requirement-statuses");
       setRequirementStatuses(res.data);
      } catch (e) {
        // keep fallback REQUIREMENT_STATUSES
      }
    }

    fetchRequirementStatuses();
   
  }, []);


  return (
    <div>
        <div className="p-2 flex justify-end">
          {/* <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Manage Funding </h1> */}
          {/* <p className="mt-2 text-muted-foreground max-w-2xl">
            Capture Leasing and Hybrid requirements, tag multiple lenders, and track statuses, documents, and timelines in one place.
          </p> */}
          <div className="mt-2 mb-2">
            <Button
              variant="hero"
              size="lg"
              onClick={() => {
                setEditingRequirement(null);
                setFormOpen(true);
              }}
              disabled={isAddingRequirement || isFetchingRequirements}
            >
              {isAddingRequirement ? "Adding..." : "Add Requirement"}
            </Button>
          </div>
        </div>

      <main className="pb-16 px-4">
        {(isFetchingRequirements || isAddingRequirement) && (
          <div className="mb-3 text-xs text-muted-foreground">
            {isFetchingRequirements ? "Loading requirements..." : "Refreshing requirements..."}
          </div>
        )}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="overflow-hidden ">
            <CardContent className="p-4 bg-gradient-to-br from-primary/5 to-transparent">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-muted-foreground">Total Requirements</div>
                  <div className="mt-1 text-2xl font-semibold">{kpis.totalReq}</div>
                </div>
                <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                  <Clipboard className="h-5 w-5" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="overflow-hidden">
            <CardContent className="p-4 bg-gradient-to-br from-primary/5 to-transparent">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-muted-foreground">Total Sanctioned</div>
                  <div className="mt-1 text-2xl font-semibold">{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(kpis.totalSanctioned)}</div>
                </div>
                <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                  <BadgeCheck className="h-5 w-5" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="overflow-hidden">
            <CardContent className="p-4 bg-gradient-to-br from-primary/5 to-transparent">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-muted-foreground">Total Disbursed</div>
                  <div className="mt-1 text-2xl font-semibold">{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(kpis.totalDisbursed)}</div>
                </div>
                <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                  <IndianRupee className="h-5 w-5" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="overflow-hidden">
            <CardContent className="p-4 bg-gradient-to-br from-primary/5 to-transparent">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-muted-foreground">Total Revenue</div>
                  <div className="mt-1 text-2xl font-semibold">{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(kpis.totalRevenue)}</div>
                </div>
                <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                  <TrendingUp className="h-5 w-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
        {state?.requirements?.length === 0 ? (
          <section className="border rounded-xl p-10 text-center bg-card">
            <h2 className="text-xl font-semibold">No requirement created</h2>
            <p className="text-muted-foreground mt-1">Start by adding your first funding requirement.</p>
            <Button className="mt-4" onClick={() => {
              setEditingRequirement(null);
              setFormOpen(true);
            }}>
              Add Requirement
            </Button>
          </section>
        ) : (
          <section>
            <div className="space-y-4">
             
              {state?.requirements?.map((r) => (
                <RequirementCard key={r.id} req={r as any} onUpdate={updateRequirement} onView={(requirementData) => handleViewRequirement(requirementData)} 
                requirementStatuses={requirementStatuses}
                uniqueRmNames={adminUserList}
                lenderStatusList={LenderStatus}
                />
              ))}

            {/* <RequirementCard key={state?.requirements[0]?.id} req={state?.requirements[0] as any} onUpdate={updateRequirement} onView={(requirementData) => handleViewRequirement(requirementData)}  /> */}
            </div>
          </section>
        )}
      </main>

      <RequirementForm
        LenderStatus={LenderStatus}
        open={formOpen}
        onOpenChange={handleFormOpenChange}
        onSubmit={handleSubmitRequirement}
        isSubmitting={isAddingRequirement}
        requirement={editingRequirement}
        uniqueRmNames={adminUserList}
        recentQuantum={recentQuantum}
        lender_user_product_types={lender_user_product_types}
      />

      <RequirementDetailPanel
        adminUserList={adminUserList}
        open={!!selectedRequirement}
        onOpenChange={(v) => !v && setDetailId(null)}
        requirement={selectedRequirement}
        onUpdate={updateRequirement}
      />

      <EnhancedViewTaggedLenderDialog
        open={!!viewLender}
        onOpenChange={(v) => !v && setViewLender(null)}
        lender={viewLender}
        onNoteUpdate={(lenderId, note) => {
          setViewLender(prev => prev ? { ...prev, note } : null);
        }}
        LenderStatus={LenderStatus}
      />




      <LenderStatusDialog
        open={!!statusOpenFor}
        onOpenChange={(v) => !v && setStatusOpenFor(null)}
        lenderInstitutionName={currentLender?.institutionName}
        lenderUserName={currentLender?.lenderUserName}
        currentStatus={currentLender?.statusHistory[currentLender.statusHistory.length - 1]?.statusId}
        sanctionedAmountFromHistory={
          currentLender
            ? [...currentLender.statusHistory]
              .reverse()
              .find(e => typeof e.sanctionAmount === 'number')
              ?.sanctionAmount
            : undefined
        }
        onSubmit={(vals) => statusOpenFor && handleStatusUpdate(statusOpenFor, vals)}
        LenderStatus={LenderStatus}
      />
    </div>
  );
};

export default Login;
